// HQ → Message templates: every WhatsApp template on the organization's accounts (synced from Meta), with its
// status, quality and the automations that use it. HQ writes new templates (blanks filled from data fields, reply
// and link buttons), sends them to Meta for approval, copies them into other languages or accounts, adds the
// starter pack to an account and sends a test. Meta's rules are checked here before anything is submitted.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import api, { formatDateTime } from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import { ChipRow, Confirm, EmptyState, Loader, Modal, PageHead, SearchBox, Spinner } from '../components/ui';

const STATUS_TONE = { approved: 'ok', pending: 'warn', draft: 'info', rejected: 'danger', paused: 'warn', disabled: 'muted', deleted: 'muted' };
const STATUS_CHIPS = [
  { key: '', label: 'All' },
  { key: 'approved', label: 'Approved' },
  { key: 'pending', label: 'In review' },
  { key: 'draft', label: 'Drafts' },
  { key: 'rejected', label: 'Rejected' },
];
const EMPTY = {
  name: '',
  language: 'en',
  category: 'UTILITY',
  header_type: '',
  header_text: '',
  body: '',
  footer: '',
  buttons: [],
  body_fields: [],
  header_fields: [],
};
const PLACEHOLDER = /\{\{(\d+)\}\}/g;

function placeholders(text) {
  const out = [];
  String(text || '').replace(PLACEHOLDER, (_, n) => {
    if (!out.includes(Number(n))) out.push(Number(n));
    return '';
  });
  return out.sort((a, b) => a - b);
}

function fill(text, values) {
  return String(text || '').replace(PLACEHOLDER, (m, n) => values[Number(n) - 1] || m);
}

function StatusPill({ status }) {
  return <span className={`de-pill de-pill--sm de-pill--${STATUS_TONE[status] || 'muted'}`}>{status === 'pending' ? 'in review' : status}</span>;
}

/** WhatsApp-style bubble with the sample values filled in. */
function Preview({ form, fields }) {
  const sample = (key) => (key ? fields.find((f) => f.key === key)?.sample || key : ''); // no field yet: {{n}} stays
  const body = fill(form.body, (form.body_fields || []).map(sample));
  const header = form.header_type === 'text' ? fill(form.header_text, (form.header_fields || []).map(sample)) : '';
  return (
    <div style={{ background: '#e5ddd5', borderRadius: 10, padding: 14 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', boxShadow: '0 1px 1px rgba(0,0,0,.08)', maxWidth: 360 }}>
        {form.header_type && form.header_type !== 'text' ? (
          <div className="de-small de-muted" style={{ marginBottom: 6 }}>
            [{form.header_type}]
          </div>
        ) : null}
        {header ? <div style={{ fontWeight: 700, marginBottom: 4 }}>{header}</div> : null}
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{body || <span className="de-muted">Message text…</span>}</div>
        {form.footer ? <div className="de-small de-muted" style={{ marginTop: 6 }}>{form.footer}</div> : null}
      </div>
      {(form.buttons || []).map((b, i) => (
        <div key={i} style={{ background: '#fff', borderRadius: 8, marginTop: 4, padding: 8, textAlign: 'center', color: '#0a7cff', fontSize: 14, maxWidth: 360 }}>
          {b.type === 'url' ? '↗ ' : b.type === 'phone_number' ? '☎ ' : ''}
          {b.text || (b.type === 'copy_code' ? 'Copy code' : 'Button')}
        </div>
      ))}
    </div>
  );
}

function FieldPicker({ fields, value, onChange }) {
  const groups = useMemo(() => {
    const out = {};
    fields.forEach((f) => {
      (out[f.group] = out[f.group] || []).push(f);
    });
    return out;
  }, [fields]);
  return (
    <select className="de-select" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose what fills it…</option>
      {Object.entries(groups).map(([group, items]) => (
        <optgroup key={group} label={group}>
          {items.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label} (e.g. {f.sample})
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function ButtonsEditor({ buttons, onChange, fields }) {
  const set = (i, patch) => onChange(buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div>
      {buttons.map((b, i) => (
        <div key={i} className="de-row" style={{ gap: 8, marginBottom: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <select className="de-select" style={{ width: 150 }} value={b.type} onChange={(e) => set(i, { type: e.target.value })}>
            <option value="quick_reply">Reply button</option>
            <option value="url">Link</option>
            <option value="phone_number">Call</option>
            <option value="copy_code">Copy code</option>
          </select>
          {b.type !== 'copy_code' ? (
            <input className="de-input de-input--bordered" style={{ width: 180 }} maxLength={25} value={b.text || ''} placeholder="Button text" onChange={(e) => set(i, { text: e.target.value })} />
          ) : null}
          {b.type === 'url' ? (
            <>
              <input className="de-input de-input--bordered" style={{ flex: 1, minWidth: 200 }} value={b.url || ''} placeholder="https://… (may end with {{1}})" onChange={(e) => set(i, { url: e.target.value })} />
              {String(b.url || '').endsWith('{{1}}') ? (
                <div style={{ width: 240 }}>
                  <FieldPicker fields={fields} value={b.url_field} onChange={(v) => set(i, { url_field: v })} />
                </div>
              ) : null}
            </>
          ) : null}
          {b.type === 'phone_number' ? (
            <input className="de-input de-input--bordered" style={{ width: 170 }} value={b.phone_number || ''} placeholder="+91…" onChange={(e) => set(i, { phone_number: e.target.value })} />
          ) : null}
          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => onChange(buttons.filter((_, j) => j !== i))}>
            Remove
          </button>
        </div>
      ))}
      {buttons.length < 10 ? (
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => onChange([...buttons, { type: 'quick_reply', text: '' }])}>
          + Add button
        </button>
      ) : null}
      <div className="de-small de-muted de-mt-8">
        Reply buttons come back to the automation that sent the message (each can lead to its own step). Up to 3 show as buttons inside the 24-hour window.
      </div>
    </div>
  );
}

function TemplateEditor({ initial, meta, accounts, onClose, onSaved }) {
  const editing = Boolean(initial?.id);
  const locked = editing && ['approved', 'pending'].includes(initial.status);
  const [form, setForm] = useState(editing ? { ...EMPTY, ...initial } : { ...EMPTY, waba_id: accounts[0]?.waba_id || '' });
  const [problems, setProblems] = useState(initial?.problems || []);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const bodyBlanks = placeholders(form.body);
  const headerBlanks = form.header_type === 'text' ? placeholders(form.header_text) : [];

  const setBlank = (key, index, value) =>
    setForm((f) => {
      const list = [...(f[key] || [])];
      list[index] = value;
      return { ...f, [key]: list };
    });

  const save = async (submit) => {
    setSaving(true);
    setProblems([]);
    const payload = {
      ...form,
      body_fields: bodyBlanks.map((n) => (form.body_fields || [])[n - 1] || ''),
      header_fields: headerBlanks.map((n) => (form.header_fields || [])[n - 1] || ''),
    };
    try {
      let row;
      if (editing) {
        const { name, language, waba_id, ...patch } = payload;
        row = await api.updateWaTemplate(initial.id, {
          category: patch.category, header_type: patch.header_type, header_text: patch.header_text, body: patch.body,
          footer: patch.footer, buttons: patch.buttons, body_fields: patch.body_fields, header_fields: patch.header_fields,
        });
        if (row.problems?.length) {
          setProblems(row.problems);
          if (submit) throw new Error('Fix the problems before sending it to Meta');
        }
        if (submit) row = await api.submitWaTemplate(initial.id);
      } else {
        row = await api.createWaTemplate({ ...payload, submit });
        if (row.submit_error) throw new Error(`Saved as a draft. Meta refused it: ${row.submit_error}`);
      }
      toast.success(submit ? 'Sent to Meta for approval' : 'Saved');
      onSaved(row);
    } catch (e) {
      if (e.data?.problems) setProblems(e.data.problems);
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? `${initial.name} (${initial.language})` : 'New message template'} onClose={onClose} size="wide">
      {locked ? (
        <div className="de-callout" style={{ marginBottom: 12 }}>
          This template is {initial.status === 'approved' ? 'approved' : 'in review'}. Saving changes and sending it again puts it back into Meta’s review.
        </div>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 20 }}>
        <div>
          <div className="de-form-grid de-form-grid--2">
            <label className="de-field">
              <span className="de-label">Name * (lowercase, numbers, _)</span>
              <input className="de-input de-input--bordered de-mono" value={form.name} disabled={editing} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} />
            </label>
            <label className="de-field">
              <span className="de-label">Language</span>
              <select className="de-select" value={form.language} disabled={editing} onChange={set('language')}>
                {Object.entries(meta.languages).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label} ({code})
                  </option>
                ))}
              </select>
            </label>
            <label className="de-field">
              <span className="de-label">Category</span>
              <select className="de-select" value={form.category} disabled={editing && Boolean(initial.meta_template_id)} onChange={set('category')}>
                <option value="UTILITY">Utility (bookings, reminders)</option>
                <option value="MARKETING">Marketing (offers, win-backs)</option>
              </select>
            </label>
            <label className="de-field">
              <span className="de-label">WhatsApp account</span>
              <select className="de-select" value={form.waba_id} disabled={editing} onChange={set('waba_id')}>
                {accounts.map((a) => (
                  <option key={a.waba_id} value={a.waba_id}>
                    {a.number} · {a.waba_id}
                  </option>
                ))}
              </select>
            </label>
            <label className="de-field">
              <span className="de-label">Header</span>
              <select className="de-select" value={form.header_type} onChange={set('header_type')}>
                <option value="">None</option>
                <option value="text">Text</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="document">Document (PDF)</option>
              </select>
            </label>
            {form.header_type === 'text' ? (
              <label className="de-field">
                <span className="de-label">Header text (max 60, one {'{{1}}'})</span>
                <input className="de-input de-input--bordered" maxLength={60} value={form.header_text} onChange={set('header_text')} />
              </label>
            ) : (
              <div />
            )}
            <label className="de-field" style={{ gridColumn: '1 / -1' }}>
              <span className="de-label">Message * (use {'{{1}}'}, {'{{2}}'}… for the parts that change)</span>
              <textarea className="de-input de-input--bordered" rows={5} maxLength={1024} value={form.body} onChange={set('body')} />
              <span className="de-small de-muted">{form.body.length} / 1024</span>
            </label>
          </div>
          {[...headerBlanks.map((n) => ['header_fields', n, 'Header']), ...bodyBlanks.map((n) => ['body_fields', n, 'Message'])].map(([key, n, where]) => (
            <div key={`${key}${n}`} className="de-row" style={{ gap: 10, marginTop: 8, alignItems: 'center' }}>
              <span className="de-small de-mono" style={{ width: 120 }}>
                {where} {`{{${n}}}`}
              </span>
              <div style={{ flex: 1 }}>
                <FieldPicker fields={meta.fields} value={(form[key] || [])[n - 1]} onChange={(v) => setBlank(key, n - 1, v)} />
              </div>
            </div>
          ))}
          <label className="de-field de-mt-16">
            <span className="de-label">Footer (optional, max 60)</span>
            <input className="de-input de-input--bordered" maxLength={60} value={form.footer} onChange={set('footer')} />
          </label>
          <div className="de-section-label de-mt-16">Buttons</div>
          <ButtonsEditor buttons={form.buttons || []} fields={meta.fields} onChange={(buttons) => setForm((f) => ({ ...f, buttons }))} />
        </div>
        <div>
          <div className="de-section-label">Preview</div>
          <Preview form={{ ...form, body_fields: bodyBlanks.map((n) => (form.body_fields || [])[n - 1]), header_fields: headerBlanks.map((n) => (form.header_fields || [])[n - 1]) }} fields={meta.fields} />
          {problems.length ? (
            <div className="de-callout de-mt-16" style={{ borderColor: 'var(--de-danger)' }}>
              <div className="de-td-strong">Meta would refuse this:</div>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {problems.map((p) => (
                  <li key={p} className="de-small">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {initial?.rejected_reason ? <div className="de-callout de-mt-16">Meta rejected it: {initial.rejected_reason}</div> : null}
          {initial?.used_by?.length ? (
            <div className="de-small de-mt-16">
              <span className="de-muted">Used by: </span>
              {initial.used_by.map((a) => a.name).join(', ')}
            </div>
          ) : null}
        </div>
      </div>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--outline" disabled={saving || !form.name || !form.body} onClick={() => save(false)}>
          Save draft
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={saving || !form.name || !form.body} onClick={() => save(true)}>
          {saving ? <Spinner light /> : null}
          Save and send to Meta
        </button>
      </div>
    </Modal>
  );
}

function DuplicateModal({ row, meta, accounts, onClose, onSaved }) {
  const [form, setForm] = useState({ language: row.language === 'en' ? 'ta' : 'en', waba_id: row.waba_id, name: row.name });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const copy = await api.duplicateWaTemplate(row.id, form);
      toast.success('Copied as a draft: translate the text, then send it to Meta');
      onSaved(copy);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Copy ${row.name}`} onClose={onClose}>
      <div className="de-form-grid">
        <label className="de-field">
          <span className="de-label">Language</span>
          <select className="de-select" value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
            {Object.entries(meta.languages).map(([code, label]) => (
              <option key={code} value={code}>
                {label} ({code})
              </option>
            ))}
          </select>
        </label>
        <label className="de-field">
          <span className="de-label">Account</span>
          <select className="de-select" value={form.waba_id} onChange={(e) => setForm((f) => ({ ...f, waba_id: e.target.value }))}>
            {accounts.map((a) => (
              <option key={a.waba_id} value={a.waba_id}>
                {a.number} · {a.waba_id}
              </option>
            ))}
          </select>
        </label>
        <label className="de-field">
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered de-mono" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </label>
      </div>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={busy} onClick={save}>
          {busy ? <Spinner light /> : null}
          Copy
        </button>
      </div>
    </Modal>
  );
}

function TestModal({ row, onClose }) {
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.testWaTemplate(row.id, phone);
      if (r.ok === false) toast.error(r.error || 'Not sent');
      else toast.success(`Sent as ${r.sent_as || 'a message'}`);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Send ${row.name} as a test`} onClose={onClose}>
      <div className="de-small de-muted" style={{ marginBottom: 10 }}>
        Inside that number’s 24-hour window it goes as text with buttons; otherwise the template must be approved.
      </div>
      <input className="de-input de-input--bordered" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile number" />
      <div className="de-form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={phone.replace(/\D/g, '').length < 10 || busy} onClick={send}>
          {busy ? <Spinner light /> : null}
          Send test
        </button>
      </div>
    </Modal>
  );
}

export default function WaTemplates() {
  const { canManage } = useEngagementScope();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [params] = useSearchParams();
  const [status, setStatus] = useState('');
  // ?filter=reminders (older links) opens the same filter.
  const [usedOnly, setUsedOnly] = useState(['automations', 'reminders'].includes(params.get('filter')));
  const [account, setAccount] = useState('');
  const [q, setQ] = useState(params.get('q') || '');
  const [editing, setEditing] = useState(null);
  const [copying, setCopying] = useState(null);
  const [testing, setTesting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () =>
    Promise.all([api.listWaTemplates(), api.templateAccounts()])
      .then(([list, acc]) => {
        setRows(list);
        setAccounts(acc.accounts || []);
      })
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
    api.templateFields().then(setMeta).catch((e) => toast.error(e.message));
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).filter(
      (r) =>
        (!status || r.status === status) &&
        (!usedOnly || r.used_by?.length || r.required) &&
        (!account || r.waba_id === account) &&
        (!needle || r.name.includes(needle) || r.body.toLowerCase().includes(needle)),
    );
  }, [rows, status, usedOnly, account, q]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(typeof ok === 'function' ? ok(r) : ok);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  const openEditor = async (row) => {
    try {
      setEditing(await api.getWaTemplate(row.id));
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!rows || !meta) return <Loader />;
  return (
    <div className="de-fade">
      <PageHead
        title="Message templates"
        sub="Every WhatsApp template on your accounts. Messages outside the 24-hour window can only use approved templates."
        action={
          canManage ? (
            <div className="de-row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="de-btn de-btn--outline" disabled={busy === 'sync'} onClick={() => run('sync', api.syncWaTemplates, 'Read from Meta')}>
                {busy === 'sync' ? <Spinner /> : null}
                Sync from Meta
              </button>
              <button
                type="button"
                className="de-btn de-btn--outline"
                disabled={busy === 'seed'}
                onClick={() =>
                  run('seed', () => api.seedWaTemplates({ waba_id: account, submit: false }), (r) => {
                    const made = (r.accounts || []).reduce((n, a) => n + a.created.length, 0);
                    return made ? `${made} starter templates added as drafts` : 'The starter pack is already on the account';
                  })
                }
              >
                Add starter pack
              </button>
              <button type="button" className="de-btn de-btn--primary" onClick={() => setEditing({})} disabled={!accounts.length}>
                + New template
              </button>
            </div>
          ) : null
        }
      />
      <div className="de-row" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {accounts.map((a) => (
          <button key={a.waba_id} type="button" className={`de-chip${account === a.waba_id ? ' active' : ''}`} onClick={() => setAccount(account === a.waba_id ? '' : a.waba_id)}>
            {a.number} · {a.templates} templates
            {a.by_status?.approved ? ` · ${a.by_status.approved} approved` : ''}
          </button>
        ))}
      </div>
      <div className="de-row" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <ChipRow chips={STATUS_CHIPS} value={status} onChange={setStatus} />
        <button type="button" className={`de-chip${usedOnly ? ' active' : ''}`} onClick={() => setUsedOnly((v) => !v)} title="Templates an automation or the staff alert sends: keep them approved">
          ⚡ Used by automations
        </button>
        <SearchBox value={q} onChange={setQ} placeholder="Search name or text…" />
      </div>
      {shown.length ? (
        <div className="de-table-shell">
          <div className="de-table-wrap">
            <table className="de-table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Used by</th>
                  <th>Updated</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td className="de-td-strong">
                      <span className="de-mono">{r.name}</span>
                      <div className="de-small de-muted">
                        {meta.languages[r.language] || r.language} · {r.category === 'MARKETING' ? 'Marketing' : 'Utility'}
                        {r.origin === 'seed' ? ' · starter' : ''}
                        {r.required ? ' · keep approved' : ''}
                        {r.buttons?.length ? ` · ${r.buttons.length} button${r.buttons.length > 1 ? 's' : ''}` : ''}
                      </div>
                    </td>
                    <td className="de-small" style={{ maxWidth: 380 }} title={r.preview}>
                      {r.preview.length > 140 ? `${r.preview.slice(0, 140)}…` : r.preview}
                    </td>
                    <td>
                      <StatusPill status={r.status} />
                      {r.quality && r.quality !== 'UNKNOWN' ? <div className="de-small de-muted">Quality {r.quality}</div> : null}
                      {r.rejected_reason ? <div className="de-small de-danger-text" title={r.rejected_reason}>{r.rejected_reason.slice(0, 40)}</div> : null}
                    </td>
                    <td className="de-small">{r.used_by?.length ? r.used_by.map((a) => a.name).join(', ') : <span className="de-muted">—</span>}</td>
                    <td className="de-small de-muted">{r.updated_at ? formatDateTime(r.updated_at) : '—'}</td>
                    <td className="de-td-right" style={{ whiteSpace: 'nowrap' }}>
                      {canManage ? (
                        <div className="de-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => openEditor(r)}>
                            {['draft', 'rejected'].includes(r.status) ? 'Edit' : 'Open'}
                          </button>
                          {['draft', 'rejected'].includes(r.status) ? (
                            <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `s${r.id}`} onClick={() => run(`s${r.id}`, () => api.submitWaTemplate(r.id), 'Sent to Meta for approval')}>
                              Send to Meta
                            </button>
                          ) : null}
                          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setCopying(r)}>
                            Copy
                          </button>
                          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setTesting(r)}>
                            Test
                          </button>
                          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDeleting(r)}>
                            Delete
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState icon="📝" title="No templates here" body="Sync from Meta, add the starter pack or write a new one." />
      )}

      {editing ? (
        <TemplateEditor
          initial={editing.id ? editing : null}
          meta={meta}
          accounts={accounts}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}
      {copying ? (
        <DuplicateModal
          row={copying}
          meta={meta}
          accounts={accounts}
          onClose={() => setCopying(null)}
          onSaved={(copy) => {
            setCopying(null);
            load().then(() => openEditor(copy));
          }}
        />
      ) : null}
      {testing ? <TestModal row={testing} onClose={() => setTesting(null)} /> : null}
      {deleting ? (
        <Confirm
          title={`Delete ${deleting.name}?`}
          message="It is deleted on Meta too, and Meta keeps the name blocked for 30 days. Templates used by an active automation cannot be deleted."
          confirmLabel="Delete"
          busy={busy === 'del'}
          onCancel={() => setDeleting(null)}
          onConfirm={() => run('del', () => api.deleteWaTemplate(deleting.id), 'Deleted').finally(() => setDeleting(null))}
        />
      ) : null}
    </div>
  );
}
